<template>
    <div class="edit-contact-container">
        <div class="section-wrapper">
            <div class="section-container">
                <label class="edit-contact-label">Display Name</label>
                <div class="input-container">
                    <prime-input-text class="input-field" placeholder="John Doe" v-model="contactName" @keydown.enter="onEnter" @keydown="letterOnlyInputHandler" />
                </div>
            </div>
        </div>

        <!-- Show notice for linked contacts -->
        <div v-if="isLinkedContact" class="linked-contact-notice">
            <div class="notice-content">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="notice-text">This contact is linked to a trusted person. Only the display name can be edited.</span>
            </div>
        </div>

        <div v-if="!isLinkedContact" class="w-full">
            <div v-for="(email, index) in emailAdd" :key="index" class="section-wrapper">
            <div class="section-container">
                <label class="edit-contact-label">
                    Email Address
                    <span v-if="index === 0" class="text-red-500 ml-1">*</span>
                </label>
                <div class="input-container">
                    <span class="pl-3 pr-2 text-gray-500">
                        <EnvelopeIcon class="h-5 w-5 text-gray-500" />
                    </span>
                    <prime-input-text
                        v-model="emailAdd[index]"
                        class="input-field !px-0"
                        placeholder="johndoe@email.com"
                        :class="{
                            'border-red-500': (index === 0 && !emailAdd[index]) ||
                                            (emailAdd[index] && emailAdd[index].trim() && !isValidEmail(emailAdd[index].trim())),
                            'opacity-50 cursor-not-allowed': isLinkedContact
                        }"
                        :disabled="isLinkedContact"
                        required
                        @keydown.enter="onEnter"
                    />
                    <button v-if="emailAdd[index] && !isLinkedContact" :disabled="emailAdd.length === 1" @click="removeEmail(index)" class="delete-item-btn">
                        <TrashIcon class="h-5 w-5 text-gray-500" />
                    </button>
                </div>
                <!-- Show validation message for first email only (when creating new contact, not for linked contacts) -->
                <div v-if="index === 0 && emailAdd[index] && emailAdd[index].trim() && !isLinkedContact && !props.contact?.id" class="validation-message">
                    <span v-if="isValidEmail(emailAdd[index].trim())" class="text-green-600 text-xs">
                        ✓ Valid email address
                    </span>
                    <span v-else class="text-red-500 text-xs">
                        ✗ Please enter a valid email address
                    </span>
                </div>
            </div>

            <div v-if="emailAdd.length < 5 && !isLinkedContact" class="add-another-btn-container">
            <PlusIcon class="w-5 h-5 text-blue-600" />
            <p
                class="add-another-btn"
                :class="{ 'opacity-50 cursor-not-allowed': !canAddMoreEmail }"
                @click="canAddMoreEmail ? addEmail() : null"
            >
                Add Email
            </p>
        </div>

        <div v-for="(number, index) in phoneNumber" :key="index" class="section-wrapper">
            <div class="section-container">
                <label class="edit-contact-label">
                    Phone Number<span v-if="!isLinkedContact"> (Optional)</span>
                </label>
                <div class="input-container">
                    <PrimeSelect
                        v-model="phoneNumber[index].country_code"
                        :options="visibleCountryOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="US +1"
                        panelClass="country-select-panel"
                        class="whitespace-nowrap"
                        :class="{ 'opacity-50 cursor-not-allowed': isLinkedContact }"
                        :disabled="isLinkedContact"
                        @show="onCountryDropdownShow"
                        @hide="onCountryDropdownHide"
                    >
                        <template #value="slotProps">
                            <div v-if="slotProps.value && slotProps.value.code" class="flex items-center">
                                <span>{{ slotProps.value.dial_code }}</span>
                            </div>
                            <span v-else>{{ slotProps.placeholder }}</span>
                        </template>

                        <template #option="slotProps">
                            <div class="flex items-center">
                                <img
                                    :src="`/flags/${slotProps.option.code}.svg`"
                                    class="mr-2"
                                    style="width: 18px"
                                    :alt="slotProps.option.name"
                                    loading="lazy"
                                />
                                <span>{{ slotProps.option.label }}</span>
                            </div>
                        </template>
                    </PrimeSelect>
                    <prime-input-text
                        v-model="phoneNumber[index].phone_number"
                        class="input-field w-full"
                        :class="{ 'opacity-50 cursor-not-allowed': isLinkedContact }"
                        placeholder="555-555-5555"
                        type="number"
                        :disabled="isLinkedContact"
                    />
                    <button v-if="phoneNumber[index].phone_number && !isLinkedContact" :disabled="phoneNumber.length === 1" @click="removephoneNumber(index)" class="delete-item-btn">
                        <TrashIcon class="h-5 w-5 text-gray-500" />
                    </button>
                </div>
            </div>
        </div>

        <div v-if="phoneNumber.length < 5 && !isLinkedContact" class="add-another-btn-container">
            <PlusIcon class="w-5 h-5 text-blue-600" />
            <p
                class="add-another-btn"
                :class="{ 'opacity-50 cursor-not-allowed': !canAddMorePhone }"
                @click="canAddMorePhone ? addphoneNumber() : null"
            >
                Add Phone
            </p>
        </div>
        </div>

        </div>     
    </div>

    <!-- actions -->
    <div class="edit-contact-footer">
        <div class="footer-actions">
            <prime-button @click="close" class="p-btn-root_secondary" label="Cancel" />

            <prime-button
                label="Update Contact"
                variant="primary"
                :disabled="!canUpdateContact"
                @click="updateContact"
                @keyup.enter="updateContact"
                :loading="isUpdatingContact"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import countries from '@/assets/json/countries.json';
import { EnvelopeIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { PlusIcon } from '@heroicons/vue/24/solid';
import PrimeInputText from 'primevue/inputtext';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useContactsStore } from '~/stores/person/contacts';
import type { Contact } from '~/types/person/contacts';

const props = defineProps<{
    contact: Contact | undefined;
}>();

// Check if contact is linked to a trusted person (read-only emails/phones)
const isLinkedContact = computed(() => {
    return props.contact?.trusted_person_id != null;
});

const { letterOnlyInputHandler } = useUtils();

//handle close dialog
const emit = defineEmits(['closeDialog']);

const close = () => {
    emit('closeDialog');
};

const countryOptions = countries.map((c) => ({
    label: `${c.name} (${c.dial_code})`,
    value: c,
    code: c.code.toLowerCase(),
    name: c.name,
    dial_code: c.dial_code,
}));
const PAGE_SIZE = 10;
const visibleCountryOptions = ref(countryOptions.slice(0, PAGE_SIZE));
const countryPage = ref(1);
let scrollableEl: HTMLElement | null = null;
let scrollListener: any = null;

// Initialize form data with existing contact data
const contactName = ref('');
const emailAdd = ref<string[]>(['']);
const phoneNumber = ref<Array<{ country_code: any; phone_number: string }>>([{
    country_code: countryOptions[0].value,
    phone_number: '',
}]);

// Initialize emails
onMounted(() => {
    if (props.contact) {
        contactName.value = props.contact.name || '';
        
        if (props.contact.emails && props.contact.emails.length > 0) {
            emailAdd.value = props.contact.emails.map(email => email.email || '');
        } else {
            emailAdd.value = [''];
        }
        
        // Initialize phone numbers
        if (props.contact.phone_numbers && props.contact.phone_numbers.length > 0) {
            phoneNumber.value = props.contact.phone_numbers.map(phone => {
                // Find the matching country option for this country code
                const countryOption = countryOptions.find(option => 
                    parseInt(option.value.dial_code.replace('+', '')) === phone.country_code
                );
                return {
                    country_code: countryOption?.value || countryOptions[0].value, // Default to first option if not found
                    phone_number: phone.phone_number || ''
                };
            });
        } else {
            phoneNumber.value = [{
                country_code: countryOptions[0].value, // Default to first country (US)
                phone_number: '',
            }];
        }
    }
});

// Watch for changes in the contact prop (in case it comes later)
watch(() => props.contact, (newContact) => {
    if (newContact) {
        contactName.value = newContact.name || '';
        
        if (newContact.emails && newContact.emails.length > 0) {
            emailAdd.value = newContact.emails.map(email => email.email || '');
        } else {
            emailAdd.value = [''];
        }
        
        if (newContact.phone_numbers && newContact.phone_numbers.length > 0) {
            phoneNumber.value = newContact.phone_numbers.map(phone => {
                // Find the matching country option for this country code
                const countryOption = countryOptions.find(option => 
                    parseInt(option.value.dial_code.replace('+', '')) === phone.country_code
                );
                return {
                    country_code: countryOption?.value || countryOptions[0].value, // Default to first option if not found
                    phone_number: phone.phone_number || ''
                };
            });
        } else {
            phoneNumber.value = [{
                country_code: countryOptions[0].value, // Default to first country (US)
                phone_number: '',
            }];
        }
    }
}, { immediate: true });

const addEmail = () => {
    // Only add new email field if the last one is filled
    if (emailAdd.value[emailAdd.value.length - 1] && emailAdd.value[emailAdd.value.length - 1].trim().length > 0) {
        emailAdd.value.push('');
    }
};

const removeEmail = (index: number) => {
    emailAdd.value.splice(index, 1);
};

const addphoneNumber = () => {
    // Only add new phone field if the last one is filled
    if (phoneNumber.value[phoneNumber.value.length - 1] && phoneNumber.value[phoneNumber.value.length - 1].phone_number.trim().length > 0) {
        phoneNumber.value.push({
            country_code: countryOptions[0].value, // Default to first country (US)
            phone_number: '',
        });
    }
};

const removephoneNumber = (index: number) => {
    phoneNumber.value.splice(index, 1);
};

// Email validation function
const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

//computed, can update contact only if name is provided and at least one email is provided
const canUpdateContact = computed(() => {
    // For linked contacts, only name is required
    if (isLinkedContact.value) {
        return contactName.value.length > 0;
    }
    
    // For regular contacts, name and valid email are required
    return contactName.value.length > 0 &&
           emailAdd.value[0] &&
           emailAdd.value[0].trim().length > 0 &&
           isValidEmail(emailAdd.value[0].trim());
});

//computed, can add more email fields only if the last one is filled and valid
const canAddMoreEmail = computed(() => {
    const lastEmail = emailAdd.value[emailAdd.value.length - 1];
    return lastEmail && lastEmail.trim().length > 0 && isValidEmail(lastEmail.trim());
});

//computed, can add more phone fields only if the last one is filled
const canAddMorePhone = computed(() => {
    const lastPhone = phoneNumber.value[phoneNumber.value.length - 1];
    return lastPhone && lastPhone.phone_number && lastPhone.phone_number.trim().length > 0;
});

const { showSuccess, showError, showWarning } = useGlobalToast();
const isUpdatingContact = ref(false);
const store = useContactsStore();

//handle update contact
async function updateContact() {
    // Validate required fields
    if (!contactName.value.trim()) {
        showError('Error', 'Display name is required');
        return;
    }

    // Only validate emails for non-linked contacts
    if (!isLinkedContact.value) {
        if (!emailAdd.value[0] || !emailAdd.value[0].trim()) {
            showError('Error', 'At least one email address is required');
            return;
        }

        if (!isValidEmail(emailAdd.value[0].trim())) {
            showError('Error', 'Please enter a valid email address');
            return;
        }

        // Validate all other emails if they exist
        for (let i = 1; i < emailAdd.value.length; i++) {
            if (emailAdd.value[i] && emailAdd.value[i].trim() && !isValidEmail(emailAdd.value[i].trim())) {
                showError('Error', `Email address at position ${i + 1} is not valid`);
                return;
            }
        }
    }

    isUpdatingContact.value = true;

    //prepare data
    const postData: any = {
        name: contactName.value.trim(),
    };

    // Only include emails and phone numbers for non-linked contacts
    if (!isLinkedContact.value) {
        postData.emails = emailAdd.value.filter(email => email && email.trim().length > 0 && isValidEmail(email.trim())); // Filter out empty and invalid emails
        postData.phone_numbers = phoneNumber.value
            .filter(phone => phone.phone_number && phone.phone_number.trim().length > 0)
            .map(phone => ({
                country_code: typeof phone.country_code === 'object' 
                    ? parseInt((phone.country_code as any).dial_code.replace('+', ''))
                    : parseInt(phone.country_code),
                phone_number: phone.phone_number.trim()
            }));
    }

    if (!props.contact) {
        showError('Error', 'Contact not found');
        isUpdatingContact.value = false;
        return;
    }

    const success = await store.updateContact(props.contact.id, postData);

    if (success) {
        isUpdatingContact.value = false;
        close();
    } else {
        isUpdatingContact.value = false;
    }
}

function loadMoreCountriesIfNeeded() {
    if (!scrollableEl) return;
    if (scrollableEl.scrollTop + scrollableEl.clientHeight >= scrollableEl.scrollHeight - 10) {
        const nextPage = countryPage.value + 1;
        const nextOptions = countryOptions.slice(0, nextPage * PAGE_SIZE);
        if (nextOptions.length > visibleCountryOptions.value.length) {
            visibleCountryOptions.value = nextOptions;
            countryPage.value = nextPage;
        }
    }
}

function attachScrollListener() {
    const panel = document.querySelector('.country-select-panel');
    if (!panel) {
        return;
    }

    const scrollable = panel.querySelector('.select-list-container') as HTMLElement;
    if (scrollable) {
        scrollableEl = scrollable;
        scrollListener = () => loadMoreCountriesIfNeeded();
        scrollableEl.addEventListener('scroll', scrollListener, { passive: true });
    }
}

function detachScrollListener() {
    if (scrollableEl && scrollListener) {
        scrollableEl.removeEventListener('scroll', scrollListener);
        scrollableEl = null;
        scrollListener = null;
    }
}

function onCountryDropdownShow() {
    setTimeout(() => {
        attachScrollListener();
    }, 0);
}

function onCountryDropdownHide() {
    detachScrollListener();
}

const onEnter = (e: KeyboardEvent) => {
    if (canUpdateContact.value && !isUpdatingContact.value) {
        updateContact();
    }
};

onUnmounted(() => {
    detachScrollListener();
});
</script>

<style lang="scss" scoped>
.section-container {
    @apply flex flex-col items-start gap-1 w-full;
}
.input-container {
    @apply flex items-center w-full h-10 border border-zinc-300 rounded bg-white;
}
.delete-item-btn {
    @apply px-3 text-gray-500 hover:text-red-500 border-l border-zinc-300 h-full flex items-center cursor-pointer;
}
.input-field {
    @apply flex-1 py-2 px-3 focus:outline-none border border-zinc-300 border-x-white;
}
.add-another-btn-container {
    @apply flex justify-start items-center gap-1.5 cursor-pointer;

    .add-another-btn {
        @apply text-blue-600 font-inter text-sm font-semibold leading-5;
    }
}
.section-wrapper {
    @apply flex flex-col items-start gap-1.5 w-full;
}
.edit-contact-container {
    @apply flex flex-col items-start gap-4 px-6 self-stretch overflow-y-auto;

    &::-webkit-scrollbar {
        @apply w-2 block;
    }

    &::-webkit-scrollbar-track {
        @apply rounded-lg bg-gray-100;
    }

    &::-webkit-scrollbar-thumb {
         @apply rounded-lg bg-gray-200;
    }
}
.edit-contact-label {
    @apply text-gray-700 font-inter text-sm font-medium leading-5;
}
.validation-message {
    @apply mt-1;
}
.edit-contact-footer {
    @apply flex flex-col justify-center items-end self-stretch pt-8;

    .footer-actions {
        @apply flex justify-end items-center gap-3 flex-1 px-6 pb-6 w-full;
    }
}
.select-label{
    @apply whitespace-nowrap
}

.linked-contact-notice {
    @apply w-full p-3 bg-blue-10 border border-zinc-300 rounded-lg;

    .notice-content {
        @apply flex items-start gap-2;

        .notice-text {
            @apply text-blue-600 text-sm font-medium;
        }
    }
}
</style>
